import React, { useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Typography } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import DeleteIcon from '@material-ui/icons/Delete';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  dropzone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(6),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    '&.dragging': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  uploadIcon: {
    fontSize: 64,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  filePreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  fileIcon: {
    fontSize: 32,
    color: theme.palette.success.main,
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
  },
}));

const FileDropzone = ({ onFileSelect, onRemoveFile, selectedFile, uploading, error }) => {
  const classes = useStyles();
  const [dragging, setDragging] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  }, [onFileSelect]);

  const handleClick = () => {
    if (!uploading && !selectedFile) {
      if (fileInputRef.current) fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (selectedFile) {
    return (
      <Paper className={classes.filePreview}>
        <div className={classes.fileInfo}>
          <InsertDriveFileIcon className={classes.fileIcon} />
          <div>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {selectedFile.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatFileSize(selectedFile.size)}
            </Typography>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-light-danger"
          onClick={onRemoveFile}
          disabled={uploading}
        >
          <DeleteIcon style={{ fontSize: 18 }} />
        </button>
      </Paper>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      
      <Paper
        className={clsx(classes.dropzone, { dragging })}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <CloudUploadIcon className={classes.uploadIcon} />
        <Typography variant="h6" gutterBottom>
          Drag and drop your file here
        </Typography>
        <Typography variant="body2" color="textSecondary">
          or click to browse
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
          Accepted: .xlsx, .xls (Max 5MB)
        </Typography>
      </Paper>

      {error && (
        <Typography className={classes.error} variant="body2">
          <i className="fa fa-exclamation-circle mr-2"></i>
          {error}
        </Typography>
      )}
    </>
  );
};

export default FileDropzone;